import type { BrandImportSnapshot } from "@editor/modules/design-system/modules/brand/utils/brand-import-snapshot";
import chroma from "chroma-js";
import type { BrandAiExtractResult } from "../types/generation";

export interface BrandPrefill {
  domain: string;
  name: string;
  icon: string;
  industry: string;
  description: string;
  /**
   * Brandfetch snapshot in the shape the design-system dialog persists as
   * `settings.brandImport`. Carried so a story created after the brand was
   * picked opens with that brand already selected in Brand Import.
   */
  brandImport?: BrandImportSnapshot;
  /** Roles map onto the theme rows / brand-colour swatches. Absent when Brandfetch has no colours for this domain — consumers fall back to the default swatches. */
  colors?: { brand: string; accent: string; dark: string; light: string };
  /**
   * Every palette the AI proposed, in its [main, dark, light, secondary]
   * order — the wizard's whole selection model. The Ready step offers these
   * as complete sets: the swatch row IS the chosen set (plus black), never a
   * merge of sources, which is what kept regrowing stray white swatches.
   */
  paletteOptions?: string[][];
  /**
   * The roles brand-ai-extract assigned, carried verbatim. These are the base
   * design system; the Ready step's swatches only override what the user picks.
   * `background` is a theme slot (themeColor3), not the page background.
   */
  designRoles?: { icons: string; background: string; title: string };
  /** Every colour Brandfetch published for the domain, for the Ready swatch row. */
  brandColors?: string[];
  fonts: { headline: string; subtitle: string; body: string };
}

const titleCase = (value: string) =>
  value.replace(/[-_]/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

const DEFAULT_FONTS = {
  headline: "Geologica",
  subtitle: "Geologica",
  body: "Inter",
};

/** A value we can actually paint with — rules out "", whitespace and junk. */
export const isUsableColor = (value: string | undefined): value is string =>
  typeof value === "string" &&
  value.trim() !== "" &&
  chroma.valid(value.trim());

/**
 * White (or close enough to read as white). The brand/accent slots must never
 * resolve to this: the card and swatch row paint them on a white surface, so a
 * white "brand colour" renders as three blank circles. Same perceptual
 * threshold the Ready step uses to dedupe swatches.
 */
export const isNearWhite = (value: string | undefined): boolean =>
  isUsableColor(value) && chroma.deltaE(value.trim(), "#ffffff") < 5;

/**
 * The extract model sometimes returns hexes without the leading "#"
 * (production docpilot does). chroma parses those, so they pass the
 * usability guard — but CSS silently ignores `background-color: 1571E7`,
 * which painted the swatches transparent (the "white swatches" card). The
 * old wizard normalized every extract colour the same way.
 */
const withHashPrefix = (value: string | undefined): string | undefined => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed || trimmed.startsWith("#")) return trimmed || undefined;
  return /^[0-9a-fA-F]{3,8}$/.test(trimmed) ? `#${trimmed}` : trimmed;
};

/** Neutral fallback for brand fields Brandfetch doesn't return (fonts/industry/description). Live name, logo, and colours come from the /brandfetch record. */
export const resolveBrandPrefill = (domain: string): BrandPrefill => {
  const name = titleCase(domain.split(".")[0]);
  return {
    domain,
    name,
    icon: "",
    industry: "Business",
    description: `${name} is a brand our AI Brain recognises — its colours, fonts, and tone have been captured to style your document.`,
    fonts: DEFAULT_FONTS,
  };
};

/**
 * Map `/docpilot/brand-ai-extract` onto BrandPrefill roles.
 *
 * `designRoles` is the important half: the AI already decides which colour is
 * icons/elements, which is the theme background slot, and which is titles, so
 * those go through verbatim as the base design system (the old wizard mapped
 * exactly these three). `colors` stays only to seed the swatch row, derived
 * from set 1 — layout: [main/icon, dark/title, light, secondaryAccent].
 */
export const mapBrandAiExtractToPrefillColors = (
  extract: BrandAiExtractResult,
  // When the AI omits a font, prefer the caller's Brandfetch-derived fonts
  // over the generic defaults — a partial AI result shouldn't downgrade them.
  fallbackFonts: BrandPrefill["fonts"] = DEFAULT_FONTS,
): Pick<
  BrandPrefill,
  "colors" | "fonts" | "designRoles" | "paletteOptions"
> | null => {
  // Every well-formed set the AI proposed, not just the first — the wizard
  // offered them all as alternative palettes. A set of empty or malformed
  // strings would render as transparent swatches, so those are dropped whole.
  // Hexes are #-normalized first; a bare "1571E7" is chroma-valid but not
  // CSS-valid, and would paint transparent.
  const paletteOptions = (extract.colors ?? [])
    .map((set) => (set ?? []).map(withHashPrefix))
    .filter(
      (set): set is string[] =>
        set.length >= 4 && set.slice(0, 4).every(isUsableColor),
    )
    .map((set) => set.slice(0, 4).map((color) => color.trim()));
  const set1 = paletteOptions[0];
  if (!set1) return null;

  const [main, dark, light, secondary] = set1;
  const pick = (value: string | undefined, fallback: string) =>
    isUsableColor(value) ? value.trim() : fallback;
  const icons = pick(withHashPrefix(extract.elementsAndIconsColor), main);
  // The card's brand/accent slots paint on white, so a white AI pick there
  // reads as an empty swatch. Prefer the first role that actually shows —
  // whites are legitimate for `light` and for designRoles (backgrounds are
  // often white), just not as the palette the user is shown.
  const brand = [icons, secondary, main, dark].find(
    (color) => isUsableColor(color) && !isNearWhite(color),
  );
  const accent = [pick(secondary, main), dark].find(
    (color) => isUsableColor(color) && !isNearWhite(color),
  );
  return {
    colors: brand
      ? {
          brand,
          accent: accent ?? brand,
          dark,
          light,
        }
      : undefined,
    paletteOptions,
    designRoles: {
      icons,
      background: pick(withHashPrefix(extract.backgroundColor), light),
      title: pick(withHashPrefix(extract.titleColor), dark),
    },
    fonts: {
      headline: extract.titleFont?.trim() || fallbackFonts.headline,
      subtitle:
        extract.subtitleFont?.trim() ||
        extract.titleFont?.trim() ||
        fallbackFonts.subtitle,
      body: extract.paragraphFont?.trim() || fallbackFonts.body,
    },
  };
};
